import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Alert,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  OpenInNew as OpenInNewIcon,
  Business as OdooIcon,
} from '@mui/icons-material';

// Odoo URL for iframe embedding - proxied through nginx to avoid X-Frame-Options
const ODOO_URL = '/odoo-embed';
console.log('OdooERP: Using proxy URL:', ODOO_URL);

// Default Odoo credentials (from docker-compose.yml)
const ODOO_USERNAME = 'admin';
const ODOO_PASSWORD = 'admin';
// Optional: pick a specific Odoo database. If unset, Odoo will show the DB selector.
const ODOO_DB =
  (process.env.REACT_APP_ODOO_DB || process.env.REACT_APP_ODOO_DATABASE || 'odoo').trim();

const OdooERP: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const autoLoginAttempted = useRef(false);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFullscreen = () => {
    const iframe = document.getElementById('odoo-iframe') as HTMLIFrameElement;
    if (iframe) {
      iframe.requestFullscreen?.();
    }
  };

  const handleOpenInNew = () => {
    // Open the proxied Odoo URL in a new tab (same-origin, avoids iframe header quirks).
    const dbQ = ODOO_DB ? `?db=${encodeURIComponent(ODOO_DB)}` : '';
    window.open(`${ODOO_URL}/web${dbQ}`, '_blank');
  };

  // Odoo app shortcuts - URLs from actual Odoo instance
  // If ODOO_DB is unset, Odoo will show the DB selector; once a DB is selected,
  // the same routes work without adding ?db=...
  const dbQ = ODOO_DB ? `?db=${encodeURIComponent(ODOO_DB)}` : '';
  // Start with login page to avoid redirect issues in iframe (when a DB is selected).
  const odooApps = [
    { name: 'Home', path: `/web/login${dbQ}` },
    { name: 'Safety Incidents', path: `/web${dbQ}#menu_id=135&action=198&cids=1` },
    { name: 'Contacts', path: `/web${dbQ}#menu_id=4&action=14&cids=1` },
    { name: 'Settings', path: `/web${dbQ}#menu_id=1&action=84&cids=1` },
  ];

  const getOdooUrl = () => {
    // Get path from active tab
    const path = odooApps[activeTab]?.path || `/web/login${dbQ}`;
    // Don't add cache buster - it can break Odoo's db parameter parsing
    return `${ODOO_URL}${path}`;
  };

  // Auto-login function that fills and submits the Odoo login form
  const attemptAutoLogin = () => {
    const iframe = iframeRef.current;
    if (!iframe || autoLoginAttempted.current) return;

    try {
      // Access iframe content (should work since it's proxied through nginx - same origin)
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      // Check if we're on the login page
      const loginForm = iframeDoc.querySelector('form[action*="/web/login"]') as HTMLFormElement;
      if (!loginForm) return;

      // Check if already logged in (no login form means we're already in)
      const emailInput = iframeDoc.querySelector('input[name="login"], input[type="email"], input[placeholder*="Email" i]') as HTMLInputElement;
      const passwordInput = iframeDoc.querySelector('input[name="password"], input[type="password"]') as HTMLInputElement;
      
      if (!emailInput || !passwordInput) {
        // Already logged in or not on login page
        autoLoginAttempted.current = true;
        return;
      }

      // Fill in credentials
      emailInput.value = ODOO_USERNAME;
      passwordInput.value = ODOO_PASSWORD;

      // Trigger input events to ensure Odoo's JavaScript detects the changes
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      passwordInput.dispatchEvent(new Event('input', { bubbles: true }));

      // Small delay to ensure form is ready, then submit
      setTimeout(() => {
        const submitButton = iframeDoc.querySelector('button[type="submit"], input[type="submit"], button:contains("Log in")') as HTMLButtonElement;
        if (submitButton) {
          submitButton.click();
        } else {
          // Fallback: submit the form directly
          loginForm.submit();
        }
        autoLoginAttempted.current = true;
      }, 500);
    } catch (error) {
      // Cross-origin error or other issue - silently fail
      console.debug('Auto-login not possible (expected if iframe is cross-origin):', error);
    }
  };

  // Handle iframe load event
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      // Reset auto-login attempt flag when iframe reloads
      autoLoginAttempted.current = false;
      
      // Try auto-login after a short delay to ensure page is fully loaded
      setTimeout(() => {
        attemptAutoLogin();
      }, 1000);
    };

    iframe.addEventListener('load', handleLoad);
    
    // Also try immediately in case load event already fired
    setTimeout(() => {
      attemptAutoLogin();
    }, 1500);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [refreshKey, activeTab]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4">
            📦 Odoo ERP
          </Typography>
          <Chip label="Embedded" color="secondary" size="small" />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handleRefresh} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleFullscreen} title="Fullscreen">
            <FullscreenIcon />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNew}
          >
            Open in New Tab
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Auto-Login:</strong> Automatically logging in with default credentials. 
        Use the Safety module for incident management. If you see a database selector, create/select an Odoo database first.
      </Alert>

      {/* Quick Links Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Home" />
        <Tab label="Safety Incidents" />
        <Tab label="Contacts" />
        <Tab label="Settings" />
      </Tabs>

      {/* Odoo Embed */}
      <Paper
        sx={{
          height: 'calc(100vh - 280px)',
          minHeight: 500,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <iframe
          ref={iframeRef}
          id="odoo-iframe"
          key={refreshKey}
          src={getOdooUrl()}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          title="Odoo ERP"
        />
      </Paper>

      {/* Footer Info */}
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="textSecondary">
          <OdooIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
          Odoo 17 Community Edition
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Incident management integrated with Safety Dashboard
        </Typography>
      </Box>
    </Box>
  );
};

export default OdooERP;

